import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

// Module-level constant - calculated once, not on every render
const CURRENT_YEAR = new Date().getFullYear();

// External URLs centralized for maintainability
const EXTERNAL_URLS = {
  rsi: 'https://robertsspaceindustries.com',
  starCitizen: 'https://robertsspaceindustries.com/star-citizen',
  squadron42: 'https://robertsspaceindustries.com/squadron42',
  cloudImperium: 'https://cloudimperiumgames.com',
  discord: 'https://discord.gg/mk6jh5qfbv'
};

/**
 * Footer Component - PRD-055 #112
 * Includes CIG Fan Site Kit legal disclaimer per requirements:
 * 1. Clear statement that this is unofficial and not affiliated with CIG
 * 2. Trademark attribution for Star Citizen, Squadron 42, RSI
 * 3. Link to official RSI website
 * 4. Statement about content usage
 */
const Footer = () => {
  const [reduceMotion, setReduceMotion] = useState(
    () => document.documentElement.getAttribute('data-reduce-motion') === 'true'
  );

  const handleToggleMotion = useCallback(() => {
    const next = !reduceMotion;
    setReduceMotion(next);
    if (next) {
      document.documentElement.setAttribute('data-reduce-motion', 'true');
      localStorage.setItem('tol_reduce_motion', 'true');
    } else {
      document.documentElement.removeAttribute('data-reduce-motion');
      localStorage.removeItem('tol_reduce_motion');
    }
  }, [reduceMotion]);

  return (
    <footer className="site-footer">
      <div className="footer-content">
        {/* Main Footer Links */}
        <div className="footer-links">
          <div className="footer-section">
            <h4>The Orbital Ledger</h4>
            <ul>
              <li><Link to="/about">About</Link></li>
              <li><Link to="/faq">FAQ</Link></li>
              <li><Link to="/support">Support</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/community-rules">Community Rules</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Resources</h4>
            <ul>
              <li><Link to="/ships">Ship Database</Link></li>
              <li><Link to="/items">Equipment Database</Link></li>
              <li><Link to="/market-overview">Commodity Prices</Link></li>
              <li><Link to="/locations">Locations</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Community</h4>
            <ul>
              <li>
                <a
                  href={EXTERNAL_URLS.discord}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-discord-link"
                >
                  <img
                    src="/discord-icon.svg"
                    alt=""
                    className="footer-discord-icon"
                    width="20"
                    height="15"
                  />
                  Join our Discord
                </a>
              </li>
              <li><Link to="/crewlink">CrewLink LFG</Link></li>
              <li><Link to="/exchange">Exchange Hub</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Official Links</h4>
            <ul>
              <li>
                <a
                  href={EXTERNAL_URLS.rsi}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Roberts Space Industries
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_URLS.starCitizen}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Star Citizen
                </a>
              </li>
              <li>
                <a
                  href={EXTERNAL_URLS.squadron42}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Squadron 42
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* CIG Legal Disclaimer - Required by Fan Site Kit */}
        <div className="cig-disclaimer">
          <p className="disclaimer-main">
            This is an <strong>unofficial</strong> Star Citizen fansite, not affiliated with
            the Cloud Imperium group of companies. All content on this site not authored by
            its host or users is property of their respective owners.
          </p>
          <p className="disclaimer-trademark">
            Star Citizen®, Squadron 42®, Roberts Space Industries®, and Cloud Imperium®
            are registered trademarks of{' '}
            <a
              href={EXTERNAL_URLS.cloudImperium}
              target="_blank"
              rel="noopener noreferrer"
            >
              Cloud Imperium Rights LLC
            </a>.
          </p>
          <p className="disclaimer-usage">
            Ship specifications, images, and other Star Citizen assets are used under the
            community content guidelines. Visit{' '}
            <a
              href={EXTERNAL_URLS.rsi}
              target="_blank"
              rel="noopener noreferrer"
            >
              robertsspaceindustries.com
            </a>
            {' '}for official information.
          </p>
        </div>

        {/* Animation Toggle — PRD-090 Phase 7 */}
        <div className="footer-animation-toggle">
          <button
            type="button"
            className="footer-reduce-motion-btn"
            onClick={handleToggleMotion}
            aria-pressed={reduceMotion}
            title={reduceMotion ? 'Animations are disabled. Click to enable.' : 'Click to disable animations.'}
          >
            <span className="footer-reduce-motion-label">Reduce Animations</span>
            <span className={`footer-reduce-motion-switch ${reduceMotion ? 'footer-reduce-motion-switch--on' : ''}`} aria-hidden="true">
              <span className="footer-reduce-motion-knob" />
            </span>
          </button>
        </div>

        {/* Copyright */}
        <div className="footer-copyright">
          <p>&copy; {CURRENT_YEAR} The Orbital Ledger. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// React.memo prevents unnecessary re-renders for this static component
export default React.memo(Footer);
